import { Col, Row } from 'react-bootstrap';
import AppLoader from 'components/general/AppLoader';
import { useSelector } from 'react-redux';
import { useCallback, useContext, useState, useEffect } from "react";
import { ProfileContext } from "pages/Profile";
import Pathway from './MyPathways/Pathway';
import { loadConfig } from '../../config';
import { useLocation, useNavigate } from 'react-router-dom';

const normalizeId = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
  if (typeof value === 'object') {
    if (value._id !== undefined && value._id !== null) return String(value._id).trim();
    if (value.id !== undefined && value.id !== null) return String(value.id).trim();
  }
  return String(value).trim();
};

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const buildFallbackPathway = (assignment, fallbackId = '') => {
  const name = String(assignment?.pathway?.name || '').trim();
  const resolvedId = normalizeId(assignment?.pathway?._id) || normalizeId(assignment?.pathwayId) || fallbackId;
  if (!name && !resolvedId) return null;

  return {
    _id: resolvedId || `fallback-${name || 'pathway'}`,
    name: name || 'Pathway',
    logo: '',
    level1: { title: 'Level 1', mandatoryProjects: [], electiveProjects: [], minElectives: 0 },
    level2: { title: 'Level 2', mandatoryProjects: [], electiveProjects: [], minElectives: 0 },
    level3: { title: 'Level 3', mandatoryProjects: [], electiveProjects: [], minElectives: 0 },
    level4: { title: 'Level 4', mandatoryProjects: [], electiveProjects: [], minElectives: 0 },
    level5: { title: 'Level 5', mandatoryProjects: [], electiveProjects: [], minElectives: 0 },
  };
};

const flattenProfilePathwaysProgress = (profile) => {
  const instances = Array.isArray(profile?.pathwaysProgressByInstance)
    ? profile.pathwaysProgressByInstance
    : [];

  return instances.flatMap((instanceEntry) => {
    const instanceId = String(instanceEntry?.instanceId || '').trim();
    const pathways = Array.isArray(instanceEntry?.pathways) ? instanceEntry.pathways : [];

    return pathways.map((entry) => ({
      ...entry,
      _id: String(entry?._id || `${profile?._id || 'user'}-${instanceId || 'instance'}-${entry?.pathwayId || ''}`).trim(),
      user: String(profile?._id || ''),
      instanceId,
      pathwayId: String(entry?.pathwayId || entry?.pathway?._id || '').trim(),
      pathway: entry?.pathway || null,
      projects: Array.isArray(entry?.projects) ? entry.projects : []
    }));
  });
};

export default function MyPathways({ tabActive }) {
  const { user: authUser } = useSelector(x => x.auth);
  const { profile } = useContext(ProfileContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [userPathways, setUserPathways] = useState([]);
  const [allPathways, setAllPathways] = useState([]);
  const [pathwaysUrl, setPathwaysUrl] = useState('');

  const url = process.env.REACT_APP_URL;
  const pathwayProjectTarget = location?.state?.pathwayProjectTarget || null;

  useEffect(() => {
    loadConfig().then(config => {
      setPathwaysUrl(config.PATHWAYS_URL);
    });
  }, []);

  const fetchAllPathways = useCallback(async () => {
    if (!pathwaysUrl) return;
    try {
      const response = await fetch(`${pathwaysUrl}/pathways`);
      const data = await response.json();
      setAllPathways(Array.isArray(data) ? data : []);
    } catch {
      setAllPathways([]);
    }
  }, [pathwaysUrl]);

  useEffect(() => {
    fetchAllPathways();
  }, [fetchAllPathways]);

  const fetchUserPathways = useCallback(async ({ preferProfile = true } = {}) => {
    if (!profile?._id) return;

    const profileProgressAssignments = flattenProfilePathwaysProgress(profile);
    if (preferProfile && profileProgressAssignments.length > 0) {
      setUserPathways(profileProgressAssignments);
      return;
    }

    const response = await fetch(`${url}/userpathway/${profile._id}`, {
      headers: { Authorization: `Bearer ${authUser.token}` }
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(String(data?.error || data?.Error || `Failed to fetch pathways (${response.status})`).trim());
    }

    const normalizedPathways = Array.isArray(data)
      ? data
      : Array.isArray(data?.userPathways)
        ? data.userPathways
        : Array.isArray(data?.items)
          ? data.items
          : [];
    const profileAssignments = Array.isArray(profile?.pathwayAssignments) ? profile.pathwayAssignments : [];
    setUserPathways(normalizedPathways.length > 0 ? normalizedPathways : profileAssignments);
  }, [authUser.token, profile, url]);

  // Fetch assigned pathways for the user every time the tab becomes active
  useEffect(() => {
    if (!tabActive || !profile?._id) return;
    setLoading(true);
    fetchUserPathways()
      .catch((error) => {
        console.error('Failed to fetch user pathways:', error?.message || error);
        const profileAssignments = Array.isArray(profile?.pathwayAssignments)
          ? profile.pathwayAssignments
          : [];
        setUserPathways(profileAssignments);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [fetchUserPathways, profile?._id, profile?.pathwayAssignments, tabActive]);

  const reloadPathways = useCallback(async () => {
    await Promise.all([fetchAllPathways(), fetchUserPathways({ preferProfile: false })]);
  }, [fetchAllPathways, fetchUserPathways]);


  // Debug: log userPathways and allPathways
  // console.log('userPathways:', userPathways);
  // console.log('allPathways:', allPathways);

  // Map pathwayId to full pathway object
  const pathwayMap = {};
  allPathways.forEach(p => {
    const id = normalizeId(p?._id);
    if (id) pathwayMap[id] = p;
  });

  const handleDeepLinkConsumed = () => {
    if (!pathwayProjectTarget) return;
    const nextState = { ...(location?.state || {}) };
    delete nextState.pathwayProjectTarget;
    navigate(location.pathname, {
      replace: true,
      state: Object.keys(nextState).length > 0 ? nextState : null,
    });
  };

  return (
    <Row>
      {loading ? (
        <div className="text-center my-4">
          <AppLoader />
        </div>
      ) : userPathways.length === 0 ? (
        <>
          <p>No pathways found.</p>
          {String(profile?.pathwayAssignmentsError || '').trim() && (
            <p style={{ color: '#f9a825', fontWeight: 600 }}>
              {String(profile?.pathwayAssignmentsError || '').trim()}
            </p>
          )}
        </>
      ) : (
        userPathways.map((p) => {
          // Debug: log typeof for IDs
          // console.log('typeof p.pathwayId:', typeof p.pathwayId, 'value:', p.pathwayId);
          // console.log('typeof p._id:', typeof p._id, 'value:', p._id);
          const assignmentPathwayId = normalizeId(p?.pathwayId) || normalizeId(p?.pathway?._id);
          const assignmentPathwayName = normalizeText(p?.pathway?.name);

          let pathway = pathwayMap[assignmentPathwayId] || null;

          if (!pathway && assignmentPathwayName) {
            pathway = allPathways.find((item) => normalizeText(item?.name) === assignmentPathwayName) || null;
          }

          const effectivePathway = pathway || buildFallbackPathway(p, assignmentPathwayId);
          if (!effectivePathway) return null;

          return (
            <Col xs={12} md={6} lg={4} className="mb-4" key={p._id || p.pathwayId}>
              <Pathway
                pathway={effectivePathway}
                userPathway={p}
                onReload={reloadPathways}
                deepLinkTarget={pathwayProjectTarget}
                onDeepLinkConsumed={handleDeepLinkConsumed}
              />
              {/* {console.log(`Rendering pathway: ${pathway.name}`)} */}
            </Col>
          );
        })
      )}
    </Row>
  );
}